import React, { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { useFlexibleList } from '../../hooks/useSWR';
import { API_BASE_URL } from '../../config/apiConfig';
import { fetcher } from '../../hooks/useSWR';
import { exportToCSV } from '../../utils/exportUtils';
import { exportToExcel } from '../../components/exports/exportToExcel';
import { useTheme } from '../../contexts/ThemeContext';

const DatabaseBrowserPage = () => {
  const { theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [tableSearch, setTableSearch] = useState(''); // For filtering tables list
  const [sortColumn, setSortColumn] = useState(null); // Column name to sort by
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [visibleColumns, setVisibleColumns] = useState(new Set()); // Set of visible column names
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showRelationships, setShowRelationships] = useState(false);
  const [showTableStructure, setShowTableStructure] = useState(false);
  const [activeTab, setActiveTab] = useState('table'); // 'table' or 'query'
  const [tableStatistics, setTableStatistics] = useState(null);
  const [foreignKeys, setForeignKeys] = useState([]);
  const [referencingTables, setReferencingTables] = useState([]);
  const [queryText, setQueryText] = useState('');
  const [queryResults, setQueryResults] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const [error, setError] = useState(null);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    try {
      setPasswordError('');
      const response = await makeApiCall('POST', '/database-browser/validate-password', { password });
      if (response.valid) {
        setIsAuthenticated(true);
        setPassword('');
      } else {
        setPasswordError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch (error) {
      console.error('Password validation error:', error);
      setPasswordError(error?.message || 'Incorrect password. Please try again.');
      setPassword('');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Escape to close modals
      if (e.key === 'Escape') {
        if (showAddModal) {
          setShowAddModal(false);
          setFormData({});
          setEditingRecord(null);
        }
      }
      // Ctrl/Cmd + F to focus table search (when not in input/textarea)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && isAuthenticated) {
        const target = e.target;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          const searchInput = document.querySelector('input[placeholder="Search tables..."]');
          if (searchInput) {
            searchInput.focus();
            searchInput.select();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showAddModal, isAuthenticated]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!tableData?.data || tableData.data.length === 0) {
      setAlert({ show: true, message: 'No data to export', type: 'error' });
      return;
    }
    try {
      exportToCSV(tableData.data, `${selectedTable}_export_${new Date().toISOString().split('T')[0]}.csv`);
      setAlert({ show: true, message: 'Data exported successfully', type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setAlert({ show: true, message: 'Failed to export data', type: 'error' });
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (!tableData?.data || tableData.data.length === 0) {
      setAlert({ show: true, message: 'No data to export', type: 'error' });
      return;
    }
    try {
      // Use sorted data to match what's displayed
      const dataToExport = sortedData.length > 0 ? sortedData : tableData.data;
      
      // Convert table columns to the format expected by exportToExcel
      const excelColumns = visibleColumnsList.map(col => ({
        key: col.columnName,
        header: col.columnName,
        dataType: col.dataType,
        isPrimaryKey: col.isPrimaryKey
      }));
      
      const exportFileName = `${selectedTable}_export_${new Date().toISOString().split('T')[0]}`;
      await exportToExcel(dataToExport, excelColumns, selectedTable, exportFileName);
      setAlert({ show: true, message: 'Excel file exported successfully', type: 'success' });
    } catch (error) {
      console.error('Excel export error:', error);
      setAlert({ show: true, message: `Failed to export Excel: ${error?.message || 'Unknown error'}`, type: 'error' });
    }
  };

  // Fetch tables list
  const { data: tables = [], isLoading: tablesLoading } = useFlexibleList('/database-browser/tables');

  // Fetch table data
  const tableDataKey = selectedTable
    ? `/database-browser/tables/${selectedTable}/data?page=${page}&pageSize=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}`
    : null;
  const { data: tableData, isLoading: dataLoading, mutate: refreshData } = useSWR(tableDataKey, fetcher);

  // Fetch table columns when table is selected
  useEffect(() => {
    if (selectedTable) {
      setError(null);
      fetcher(`/database-browser/tables/${selectedTable}/columns`)
        .then((columns) => {
          setTableColumns(columns);
          setSortColumn(null);
          setSortDirection('asc');
          // Initialize all columns as visible by default
          setVisibleColumns(new Set(columns.map(col => col.columnName)));
        })
        .catch((error) => {
          console.error('Error fetching columns:', error);
          const errorMessage = error?.message || error?.info?.message || 'Failed to load table columns';
          setError(`Error loading table structure: ${errorMessage}`);
          setAlert({ 
            show: true, 
            message: `Failed to load table columns: ${errorMessage}. Please try selecting the table again.`, 
            type: 'error' 
          });
        });
    }
  }, [selectedTable]);

  // Toggle column visibility
  const toggleColumnVisibility = (columnName) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        newSet.delete(columnName);
      } else {
        newSet.add(columnName);
      }
      return newSet;
    });
  };

  // Select all columns
  const selectAllColumns = () => {
    setVisibleColumns(new Set(tableColumns.map(col => col.columnName)));
  };

  // Deselect all columns
  const deselectAllColumns = () => {
    setVisibleColumns(new Set());
  };

  // Get visible columns
  const visibleColumnsList = tableColumns.filter(col => visibleColumns.has(col.columnName));

  // Direct API calls for dynamic endpoints
  const makeApiCall = async (method, url, data = null) => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    // Check if token exists for protected endpoints
    if (!token && url.includes('/database-browser/')) {
      throw new Error('Authentication required. Please log in to access the database browser.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(userId && { 'user-id': userId }),
    };
    
    const config = {
      method,
      headers,
      ...(data && { body: JSON.stringify(data) }),
    };
    
    const response = await fetch(`${API_BASE_URL}${url}`, config);
    
    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({ message: 'Unauthorized' }));
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(errorData.message || 'Request failed');
    }
    
    if (method === 'DELETE') {
      return { message: 'Success' };
    }
    
    return response.json();
  };

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setPage(1);
    setSearch('');
    setEditingRecord(null);
    setShowAddModal(false);
    setFormData({});
    setShowStatistics(false);
    setShowRelationships(false);
    setTableStatistics(null);
    setForeignKeys([]);
    setReferencingTables([]);
  };

  // Load table statistics
  const loadTableStatistics = async () => {
    if (!selectedTable) return;
    try {
      setError(null);
      const stats = await fetcher(`/database-browser/tables/${selectedTable}/statistics`);
      setTableStatistics(stats);
      setShowStatistics(true);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setAlert({ 
        show: true, 
        message: `Failed to load table statistics: ${error?.message || 'Unknown error'}`, 
        type: 'error' 
      });
    }
  };

  // Load foreign key relationships
  const loadRelationships = async () => {
    if (!selectedTable) return;
    try {
      setError(null);
      const [fks, refs] = await Promise.all([
        fetcher(`/database-browser/tables/${selectedTable}/foreign-keys`),
        fetcher(`/database-browser/tables/${selectedTable}/referencing-tables`)
      ]);
      setForeignKeys(fks || []);
      setReferencingTables(refs || []);
      setShowRelationships(true);
    } catch (error) {
      console.error('Error loading relationships:', error);
      setAlert({ 
        show: true, 
        message: `Failed to load relationships: ${error?.message || 'Unknown error'}`, 
        type: 'error' 
      });
    }
  };

  // Execute custom SQL query
  const executeQuery = async () => {
    if (!queryText.trim()) {
      setAlert({ show: true, message: 'Please enter a SQL query', type: 'error' });
      return;
    }

    setQueryLoading(true);
    setError(null);
    try {
      const result = await makeApiCall('POST', '/database-browser/query', { 
        query: queryText,
        maxRows: 1000 
      });
      setQueryResults(result);
      setAlert({ show: true, message: `Query executed successfully. ${Array.isArray(result) ? result.length : 1} row(s) returned.`, type: 'success' });
    } catch (error) {
      console.error('Query execution error:', error);
      const errorMessage = error?.message || error?.info?.message || 'Query execution failed';
      setError(errorMessage);
      setAlert({ show: true, message: errorMessage, type: 'error' });
      setQueryResults(null);
    } finally {
      setQueryLoading(false);
    }
  };


  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowAddModal(true);
    
    // Find primary key column
    const primaryKeyColumn = tableColumns.find((col) => col.isPrimaryKey);
    if (primaryKeyColumn) {
      setFormData({ ...record, _primaryKey: primaryKeyColumn.columnName, _primaryKeyValue: record[primaryKeyColumn.columnName] });
    } else {
      setFormData(record);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setShowAddModal(true);
    setFormData({});
  };

  const handleDelete = async (record) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      const primaryKeyColumn = tableColumns.find((col) => col.isPrimaryKey);
      if (!primaryKeyColumn) {
        throw new Error('Cannot delete: Table does not have a primary key column');
      }

      const id = record[primaryKeyColumn.columnName];
      if (id === null || id === undefined) {
        throw new Error('Cannot delete: Record ID is missing');
      }

      await makeApiCall('DELETE', `/database-browser/tables/${selectedTable}/data/${id}`);
      setAlert({ show: true, message: 'Record deleted successfully', type: 'success' });
      refreshData();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error?.message || error?.info?.message || 'Failed to delete record';
      const detailedMessage = errorMessage.includes('foreign key') 
        ? 'Cannot delete: This record is referenced by other records'
        : errorMessage.includes('not found')
        ? 'Record not found: It may have been already deleted'
        : `Delete failed: ${errorMessage}`;
      setError(detailedMessage);
      setAlert({ show: true, message: detailedMessage, type: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      // Remove internal fields
      const { _primaryKey, _primaryKeyValue, ...dataToSave } = formData;

      // Validate required fields
      const requiredColumns = tableColumns.filter((col) => col.isNullable !== 'YES' && !col.columnDefault);
      const missingFields = requiredColumns.filter((col) => {
        const value = dataToSave[col.columnName];
        return value === null || value === undefined || value === '';
      });

      if (missingFields.length > 0) {
        throw new Error(`Required fields missing: ${missingFields.map((f) => f.columnName).join(', ')}`);
      }

      // Remove null/undefined values and empty strings for optional fields
      Object.keys(dataToSave).forEach((key) => {
        if (dataToSave[key] === '' || dataToSave[key] === null || dataToSave[key] === undefined) {
          const column = tableColumns.find((col) => col.columnName === key);
          if (column && column.isNullable === 'YES') {
            delete dataToSave[key];
          }
        }
      });

      if (editingRecord) {
        // Update existing record
        await makeApiCall('PATCH', `/database-browser/tables/${selectedTable}/data/${_primaryKeyValue}`, dataToSave);
        setAlert({ show: true, message: 'Record updated successfully', type: 'success' });
      } else {
        // Create new record
        await makeApiCall('POST', `/database-browser/tables/${selectedTable}/data`, dataToSave);
        setAlert({ show: true, message: 'Record created successfully', type: 'success' });
      }

      setShowAddModal(false);
      setFormData({});
      setEditingRecord(null);
      refreshData();
    } catch (error) {
      console.error('Save error:', error);
      const errorMessage = error?.message || error?.info?.message || 'Failed to save record';
      let detailedMessage = errorMessage;
      
      if (errorMessage.includes('unique constraint') || errorMessage.includes('duplicate')) {
        detailedMessage = 'Save failed: A record with this value already exists (unique constraint violation)';
      } else if (errorMessage.includes('foreign key')) {
        detailedMessage = 'Save failed: Referenced record does not exist (foreign key constraint)';
      } else if (errorMessage.includes('not null')) {
        detailedMessage = 'Save failed: Required field cannot be empty';
      } else if (errorMessage.includes('invalid input')) {
        detailedMessage = 'Save failed: Invalid data format. Please check your input values';
      }
      
      setError(detailedMessage);
      setAlert({ show: true, message: detailedMessage, type: 'error' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getInputType = (column) => {
    const dataType = column.dataType.toLowerCase();
    if (dataType.includes('int') || dataType.includes('numeric') || dataType.includes('decimal')) {
      return 'number';
    }
    if (dataType.includes('bool')) {
      return 'checkbox';
    }
    if (dataType.includes('date') || dataType.includes('time')) {
      return 'datetime-local';
    }
    if (dataType.includes('text') || dataType.includes('varchar')) {
      return 'text';
    }
    return 'text';
  };

  const formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (column.dataType.toLowerCase().includes('bool')) {
      return value ? 'Yes' : 'No';
    }
    
    // Only format as date if the column type is actually a date/time type
    const dataType = column.dataType.toLowerCase();
    const isDateTimeType = dataType.includes('date') || dataType.includes('time') || dataType.includes('timestamp');
    
    if (isDateTimeType) {
      // Try to parse as date only if it's a date/time column
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      if (typeof value === 'string') {
        // Check if it looks like a valid date string (ISO format or common date formats)
        const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
        if (dateRegex.test(value)) {
          const date = new Date(value);
          // Check if date is valid
          if (!isNaN(date.getTime())) {
            return date.toLocaleString();
          }
        }
      }
      // If it's a date column but can't be parsed, return as-is
      return String(value);
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Filter tables by search
  const filteredTables = tables.filter((table) =>
    table.tableName.toLowerCase().includes(tableSearch.toLowerCase())
  );

  // Sort table data
  const sortedData = useMemo(() => {
    if (!tableData?.data || !sortColumn) return tableData?.data || [];
    
    const sorted = [...tableData.data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      // Compare values
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
    
    return sorted;
  }, [tableData?.data, sortColumn, sortDirection]);

  const handleSort = (columnName) => {
    if (sortColumn === columnName) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };

  const totalPages = tableData?.total ? Math.ceil(tableData.total / pageSize) : 0;

  // Skeleton loader component
  const SkeletonLoader = ({ rows = 5, cols = 5 }) => (
    <div className="animate-pulse">
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-2">
            {Array.from({ length: cols }).map((_, j) => (
              <div
                key={j}
                className={`h-8 ${theme.colors.background} rounded flex-1`}
                style={{ width: `${100 / cols}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  // Show password prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.lg} p-8 max-w-md w-full ${theme.border.light} border`}>
          <h2 className={`text-2xl font-bold mb-4 text-center ${theme.text.primary}`}>Database Browser Access</h2>
          <p className={`${theme.text.secondary} mb-6 text-center`}>Please enter the password to access the database browser.</p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordSubmit(e);
                  }
                  if (e.key === 'Escape') {
                    setPassword('');
                    setPasswordError('');
                  }
                }}
                className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${theme.border.default} ${
                  passwordError ? 'border-red-500' : theme.border.light
                }`}
                placeholder="Enter password (Press Enter to submit, Esc to clear)"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className={`w-full px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded hover:opacity-90 focus:outline-none focus:ring-2 ${theme.shadow.sm}`}
            >
              Access Database Browser
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 80px)', minHeight: 0 }}>
      {alert.show && (
        <div
          className={`m-4 p-4 rounded flex-shrink-0 ${theme.border.light} border ${
            alert.type === 'error' ? theme.status.rejected : theme.status.approved
          }`}
        >
          {alert.message}
          <button
            onClick={() => setAlert({ show: false, message: '', type: 'success' })}
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className={`flex-shrink-0 border-b ${theme.colors.surface} px-6 ${theme.border.light}`}>
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('table')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'table'
                ? `${theme.colors.primary} ${theme.text.inverse} border-b-2`
                : `${theme.text.secondary} hover:${theme.text.primary}`
            }`}
          >
            Table Structure
          </button>
          <button
            onClick={() => setActiveTab('query')}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'query'
                ? `${theme.colors.primary} ${theme.text.inverse} border-b-2`
                : `${theme.text.secondary} hover:${theme.text.primary}`
            }`}
          >
            SQL Query
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden p-6 min-h-0">
        {(
          activeTab === 'table' ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full overflow-hidden min-h-0">
        {/* Tables List */}
        <div className="lg:col-span-1 flex flex-col min-h-0">
          <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.default} p-4 flex flex-col h-full min-h-0 ${theme.border.light} border`}>
            <h2 className={`text-lg font-semibold mb-4 flex-shrink-0 ${theme.text.primary}`}>Tables</h2>
            
            {/* Table Search */}
            <div className="mb-4 relative flex-shrink-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search tables..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className={`w-full px-3 py-2 pl-9 pr-8 border ${theme.border.default} rounded text-sm focus:outline-none focus:ring-2 ${theme.colors.primary} focus:${theme.border.default}`}
                />
                {/* Search Icon */}
                <svg
                  className={`absolute left-2.5 top-2.5 h-4 w-4 ${theme.text.muted}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {/* Clear Button */}
                {tableSearch && (
                  <button
                    onClick={() => setTableSearch('')}
                    className={`absolute right-2 top-2 ${theme.text.muted} hover:${theme.text.secondary}`}
                    title="Clear search"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                </svg>
                  </button>
                )}
              </div>
              {/* Search Results Count */}
              {tableSearch && filteredTables.length > 0 && (
                <div className={`mt-1 text-xs ${theme.text.muted}`}>
                  {filteredTables.length} table{filteredTables.length !== 1 ? 's' : ''} found
                </div>
              )}
            </div>

            {tablesLoading ? (
              <div className="flex-1 overflow-y-auto">
                <SkeletonLoader rows={8} cols={1} />
              </div>
            ) : filteredTables.length === 0 ? (
              <div className={`${theme.text.muted} text-sm text-center py-4 flex-1 flex items-center justify-center`}>
                {tableSearch ? `No tables found matching "${tableSearch}"` : 'No tables available'}
              </div>
            ) : (
              <div className="space-y-1 flex-1 overflow-y-auto min-h-0">
                {filteredTables.map((table) => (
                  <button
                    key={table.tableName}
                    onClick={() => handleTableSelect(table.tableName)}
                    className={`w-full text-left px-3 py-2 rounded hover:${theme.colors.backgroundHover} transition-colors ${
                      selectedTable === table.tableName ? `${theme.colors.primary} ${theme.text.inverse} font-semibold` : theme.text.primary
                    }`}
                  >
                    {table.tableName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Table Data */}
        <div className="lg:col-span-3 flex flex-col min-h-0">
          {selectedTable ? (
            <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.default} p-4 flex flex-col h-full min-h-0 ${theme.border.light} border`}>
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <div>
                  <h2 className={`text-lg font-semibold ${theme.text.primary}`}>Table: {selectedTable}</h2>
                  {tableData?.total !== undefined && (
                    <p className={`text-sm ${theme.text.muted} mt-1`}>
                      Total rows: {tableData.total.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    className={`px-4 py-2 ${theme.colors.secondary} ${theme.text.inverse} rounded hover:opacity-90 text-sm ${theme.shadow.sm}`}
                    title="Select columns to display"
                  >
                    Columns ({visibleColumnsList.length}/{tableColumns.length})
                  </button>
                  {tableData?.data && tableData.data.length > 0 && (
                    <>
                      <button
                        onClick={handleExportCSV}
                        className={`px-4 py-2 ${theme.colors.success} ${theme.text.inverse} rounded hover:opacity-90 text-sm ${theme.shadow.sm}`}
                        title="Export to CSV"
                      >
                        Export CSV
                      </button>
                      <button
                        onClick={handleExportExcel}
                        className={`px-4 py-2 ${theme.colors.success} ${theme.text.inverse} rounded hover:opacity-90 text-sm ${theme.shadow.sm}`}
                        title="Export to Excel"
                      >
                        Export Excel
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setShowStatistics(!showStatistics);
                      if (!showStatistics && selectedTable) {
                        loadTableStatistics();
                      }
                    }}
                    className={`px-4 py-2 rounded text-sm ${theme.shadow.sm} ${
                      showStatistics 
                        ? `${theme.colors.accent} ${theme.text.inverse} hover:opacity-90` 
                        : `${theme.colors.accent} ${theme.text.inverse} hover:opacity-90`
                    }`}
                    title="Show table statistics"
                  >
                    Statistics
                  </button>
                  <button
                    onClick={() => {
                      setShowRelationships(!showRelationships);
                      if (!showRelationships && selectedTable) {
                        loadRelationships();
                      }
                    }}
                    className={`px-4 py-2 rounded text-sm ${theme.shadow.sm} ${
                      showRelationships 
                        ? `${theme.colors.info} ${theme.text.inverse} hover:opacity-90` 
                        : `${theme.colors.info} ${theme.text.inverse} hover:opacity-90`
                    }`}
                    title="Show foreign key relationships"
                  >
                    Relationships
                  </button>
                  <button
                    onClick={() => setShowTableStructure(!showTableStructure)}
                    className={`px-4 py-2 rounded text-sm ${theme.shadow.sm} ${
                      showTableStructure 
                        ? `${theme.colors.accent} ${theme.text.inverse} hover:opacity-90` 
                        : `${theme.colors.accent} ${theme.text.inverse} hover:opacity-90`
                    }`}
                    title="Show table structure"
                  >
                    Structure
                  </button>
                  <button
                    onClick={handleAdd}
                    className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded hover:opacity-90 text-sm ${theme.shadow.sm}`}
                  >
                    Add Record
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="mb-4 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className={`w-full px-4 py-2 ${theme.border.default} border rounded ${theme.text.primary}`}
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className={`mb-4 p-3 ${theme.status.rejected} rounded text-sm ${theme.border.light} border`}>
                  {error}
                </div>
              )}

              {/* Table Statistics */}
              {showStatistics && tableStatistics && (
                <div className={`mb-4 p-4 ${theme.status.review} rounded ${theme.border.light} border`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`font-semibold text-sm ${theme.text.primary}`}>Table Statistics</h3>
                    <button
                      onClick={() => setShowStatistics(false)}
                      className={`${theme.text.secondary} hover:${theme.text.primary}`}
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className={theme.text.secondary}>Row Count</div>
                      <div className={`font-semibold text-lg ${theme.text.primary}`}>{tableStatistics.rowCount?.toLocaleString() || 'N/A'}</div>
                    </div>
                    <div>
                      <div className={theme.text.secondary}>Table Size</div>
                      <div className={`font-semibold ${theme.text.primary}`}>{tableStatistics.tableSize || 'N/A'}</div>
                    </div>
                    <div>
                      <div className={theme.text.secondary}>Indexes Size</div>
                      <div className={`font-semibold ${theme.text.primary}`}>{tableStatistics.indexesSize || 'N/A'}</div>
                    </div>
                    <div>
                      <div className={theme.text.secondary}>Total Size</div>
                      <div className={`font-semibold ${theme.text.primary}`}>{tableStatistics.totalSize || 'N/A'}</div>
                    </div>
                    <div>
                      <div className={theme.text.secondary}>Last Vacuum</div>
                      <div className={`font-semibold ${theme.text.primary}`}>
                        {tableStatistics.lastVacuum 
                          ? new Date(tableStatistics.lastVacuum).toLocaleString() 
                          : 'Never'}
                      </div>
                    </div>
                    <div>
                      <div className={theme.text.secondary}>Last Analyze</div>
                      <div className={`font-semibold ${theme.text.primary}`}>
                        {tableStatistics.lastAnalyze 
                          ? new Date(tableStatistics.lastAnalyze).toLocaleString() 
                          : 'Never'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Table Structure */}
              {showTableStructure && tableColumns.length > 0 && (
                <div className={`mb-4 p-4 ${theme.status.review} rounded ${theme.border.light} border`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`font-semibold text-sm ${theme.text.primary}`}>Table Structure: {selectedTable}</h3>
                    <button
                      onClick={() => setShowTableStructure(false)}
                      className={`${theme.text.secondary} hover:${theme.text.primary}`}
                    >
                      ×
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className={theme.colors.background}>
                        <tr>
                          <th className={`px-3 py-2 text-left ${theme.border.default} border font-medium ${theme.text.primary}`}>Column Name</th>
                          <th className={`px-3 py-2 text-left ${theme.border.default} border font-medium ${theme.text.primary}`}>Data Type</th>
                          <th className={`px-3 py-2 text-left ${theme.border.default} border font-medium ${theme.text.primary}`}>Nullable</th>
                          <th className={`px-3 py-2 text-left ${theme.border.default} border font-medium ${theme.text.primary}`}>Default</th>
                          <th className={`px-3 py-2 text-left ${theme.border.default} border font-medium ${theme.text.primary}`}>Primary Key</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableColumns.map((col, idx) => (
                          <tr key={idx} className={`hover:${theme.colors.backgroundHover}`}>
                            <td className={`px-3 py-2 ${theme.border.default} border`}>
                              <span className={`font-medium ${theme.text.primary}`}>{col.columnName}</span>
                            </td>
                            <td className={`px-3 py-2 ${theme.border.default} border`}>
                              <span className={theme.text.secondary}>{col.dataType}</span>
                            </td>
                            <td className={`px-3 py-2 ${theme.border.default} border`}>
                              {col.isNullable === 'YES' ? (
                                <span className={theme.status.approved.split(' ')[1]}>Yes</span>
                              ) : (
                                <span className={`${theme.status.rejected.split(' ')[1]} font-medium`}>No</span>
                              )}
                            </td>
                            <td className={`px-3 py-2 ${theme.border.default} border`}>
                              {col.columnDefault ? (
                                <span className={`${theme.text.secondary} font-mono text-xs`}>{col.columnDefault}</span>
                              ) : (
                                <span className={theme.text.muted}>—</span>
                              )}
                            </td>
                            <td className={`px-3 py-2 ${theme.border.default} border`}>
                              {col.isPrimaryKey ? (
                                <span className={`${theme.status.review} px-2 py-1 rounded text-xs font-medium`}>PK</span>
                              ) : (
                                <span className={theme.text.muted}>—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={`mt-3 text-xs ${theme.text.muted}`}>
                    Total columns: {tableColumns.length} | 
                    Primary keys: {tableColumns.filter(col => col.isPrimaryKey).length} | 
                    Nullable columns: {tableColumns.filter(col => col.isNullable === 'YES').length}
                  </div>
                </div>
              )}

              {/* Foreign Key Relationships */}
              {showRelationships && (foreignKeys.length > 0 || referencingTables.length > 0) && (
                <div className={`mb-4 p-4 ${theme.status.review} rounded ${theme.border.light} border`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`font-semibold text-sm ${theme.text.primary}`}>Foreign Key Relationships</h3>
                    <button
                      onClick={() => setShowRelationships(false)}
                      className={`${theme.text.secondary} hover:${theme.text.primary}`}
                    >
                      ×
                    </button>
                  </div>
                  
                  {foreignKeys.length > 0 && (
                    <div className="mb-4">
                      <h4 className={`font-medium text-xs ${theme.text.secondary} mb-2`}>This table references:</h4>
                      <div className="space-y-1">
                        {foreignKeys.map((fk, idx) => (
                          <div key={idx} className={`text-xs ${theme.colors.surface} p-2 rounded ${theme.border.light} border`}>
                            <span className={`font-medium ${theme.text.primary}`}>{fk.columnName}</span> → 
                            <span className={`font-medium ${theme.colors.info} ${theme.text.inverse}`}>{fk.referencedTableName}.{fk.referencedColumnName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {referencingTables.length > 0 && (
                    <div>
                      <h4 className={`font-medium text-xs ${theme.text.secondary} mb-2`}>Tables that reference this table:</h4>
                      <div className="space-y-1">
                        {referencingTables.map((ref, idx) => (
                          <div key={idx} className={`text-xs ${theme.colors.surface} p-2 rounded ${theme.border.light} border`}>
                            <span className={`font-medium ${theme.colors.info} ${theme.text.inverse}`}>{ref.columnName}.{ref.referencedTableName}</span> → 
                            <span className={`font-medium ${theme.text.primary}`}>{ref.referencedColumnName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {foreignKeys.length === 0 && referencingTables.length === 0 && (
                    <div className={`text-sm ${theme.text.muted}`}>No foreign key relationships found</div>
                  )}
                </div>
              )}


              {/* Column Selector */}
              {showColumnSelector && (
                <div className={`mb-4 p-4 ${theme.colors.background} ${theme.border.light} border rounded`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`font-semibold text-sm ${theme.text.primary}`}>Select Columns to Display</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllColumns}
                        className={`px-3 py-1 text-xs ${theme.colors.primary} ${theme.text.inverse} rounded hover:opacity-90 ${theme.shadow.sm}`}
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAllColumns}
                        className={`px-3 py-1 text-xs ${theme.colors.secondary} ${theme.text.inverse} rounded hover:opacity-90 ${theme.shadow.sm}`}
                      >
                        Deselect All
                      </button>
                      <button
                        onClick={() => setShowColumnSelector(false)}
                        className={`px-3 py-1 text-xs ${theme.colors.secondary} ${theme.text.inverse} rounded hover:opacity-90 ${theme.shadow.sm}`}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {tableColumns.map((col) => (
                      <label
                        key={col.columnName}
                        className={`flex items-center gap-2 p-2 hover:${theme.colors.backgroundHover} rounded cursor-pointer`}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.has(col.columnName)}
                          onChange={() => toggleColumnVisibility(col.columnName)}
                          className={`w-4 h-4 ${theme.colors.primary} rounded focus:ring-${theme.colors.primary}`}
                        />
                        <span className={`text-sm ${theme.text.primary}`}>
                          {col.columnName}
                          {col.isPrimaryKey && (
                            <span className={`${theme.colors.primary} ${theme.text.inverse} text-xs ml-1 px-1 rounded`}>(PK)</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Table */}
              {dataLoading ? (
                <div className="flex-1 overflow-auto mb-4 min-h-0">
                  <SkeletonLoader rows={10} cols={visibleColumnsList.length || 6} />
                </div>
              ) : tableData && tableData.data && tableData.data.length > 0 ? (
                <>
                  <div className="flex-1 overflow-auto mb-4 min-h-0">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className={theme.colors.background}>
                          {visibleColumnsList.length === 0 ? (
                            <th colSpan={100} className={`${theme.border.default} border px-4 py-2 text-center ${theme.text.muted}`}>
                              No columns selected. Please select columns to display.
                            </th>
                          ) : (
                            <>
                              {visibleColumnsList.map((col) => (
                                <th
                                  key={col.columnName}
                                  className={`${theme.border.default} border px-4 py-2 text-left cursor-pointer hover:${theme.colors.backgroundHover} select-none ${theme.text.primary}`}
                                  onClick={() => handleSort(col.columnName)}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{col.columnName}</span>
                                    {col.isPrimaryKey && (
                                      <span className={`text-xs ${theme.colors.primary} ${theme.text.inverse} font-normal px-1 rounded`} title="Primary Key">(PK)</span>
                                    )}
                                    {sortColumn === col.columnName && (
                                      <span className={theme.colors.primary}>
                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              ))}
                              <th className={`${theme.border.default} border px-4 py-2 text-left ${theme.text.primary}`}>Actions</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleColumnsList.length === 0 ? (
                          <tr>
                            <td colSpan={100} className={`${theme.border.default} border px-4 py-2 text-center ${theme.text.muted}`}>
                              No columns selected
                            </td>
                          </tr>
                        ) : (
                          sortedData.map((record, idx) => {
                            const primaryKeyColumn = tableColumns.find((col) => col.isPrimaryKey);
                            const recordId = primaryKeyColumn ? record[primaryKeyColumn.columnName] : idx;
                            return (
                              <tr key={recordId} className={`hover:${theme.colors.backgroundHover}`}>
                                {visibleColumnsList.map((col) => (
                                  <td key={col.columnName} className={`${theme.border.default} border px-4 py-2 ${theme.text.primary}`}>
                                    {formatValue(record[col.columnName], col)}
                                  </td>
                                ))}
                                <td className={`${theme.border.default} border px-4 py-2`}>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEdit(record)}
                                    className={`px-2 py-1 ${theme.colors.primary} ${theme.text.inverse} rounded text-sm hover:opacity-90 ${theme.shadow.sm}`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(record)}
                                    className={`px-2 py-1 ${theme.colors.danger} ${theme.text.inverse} rounded text-sm hover:opacity-90 ${theme.shadow.sm}`}
                                  >
                                    Delete
                                  </button>
                                </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex-shrink-0 mt-4">
                    <div className="flex justify-between items-center">
                      <div className={`text-sm ${theme.text.secondary}`}>
                        Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, tableData.total)} of{' '}
                        {tableData.total} records
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className={`px-4 py-2 ${theme.border.default} border rounded disabled:opacity-50 ${theme.colors.surface} ${theme.text.primary} hover:${theme.colors.backgroundHover} ${theme.shadow.sm}`}
                        >
                          Previous
                        </button>
                        <span className={`px-4 py-2 ${theme.text.secondary}`}>
                          Page {page} of {totalPages}
                        </span>
                        <button
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          className={`px-4 py-2 ${theme.border.default} border rounded disabled:opacity-50 ${theme.colors.surface} ${theme.text.primary} hover:${theme.colors.backgroundHover} ${theme.shadow.sm}`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={theme.text.muted}>No data found</div>
              )}
            </div>
          ) : (
            <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.default} p-8 text-center ${theme.text.muted} flex items-center justify-center h-full ${theme.border.light} border`}>
              Select a table from the list to view its data
            </div>
          )}
            </div>
          </div>
        ) : activeTab === 'query' ? (
          /* SQL Query Tab */
          <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.default} p-4 flex flex-col h-full min-h-0 ${theme.border.light} border`}>
            <div className="mb-4 flex-shrink-0">
              <h2 className={`text-lg font-semibold mb-2 ${theme.text.primary}`}>SQL Query Editor</h2>
              <p className={`text-sm ${theme.text.secondary}`}>
                Execute custom SELECT queries. Only SELECT queries are allowed. Maximum 1000 rows.
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className={`mb-4 p-3 ${theme.status.rejected} rounded text-sm flex-shrink-0 ${theme.border.light} border`}>
                {error}
              </div>
            )}

            <div className="mb-4 flex-shrink-0">
              <textarea
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="SELECT * FROM table_name LIMIT 100;"
                className={`w-full px-3 py-2 border ${theme.border.default} rounded text-sm font-mono ${theme.text.primary}`}
                rows={6}
              />
            </div>

            <div className="flex gap-2 mb-4 flex-shrink-0">
              <button
                onClick={executeQuery}
                disabled={queryLoading || !queryText.trim()}
                className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm ${theme.shadow.sm}`}
              >
                {queryLoading ? 'Executing...' : 'Execute Query'}
              </button>
              <button
                onClick={() => {
                  setQueryText('');
                  setQueryResults(null);
                  setError(null);
                }}
                className={`px-4 py-2 ${theme.colors.secondary} ${theme.text.inverse} rounded hover:opacity-90 text-sm ${theme.shadow.sm}`}
              >
                Clear
              </button>
            </div>
            
            {queryResults && (
              <div className="flex-1 overflow-auto min-h-0">
                <div className={`text-sm font-medium mb-2 flex-shrink-0 ${theme.text.primary}`}>
                  Results ({Array.isArray(queryResults) ? queryResults.length : 1} row{queryResults.length !== 1 ? 's' : ''})
                </div>
                <div className={`overflow-auto ${theme.border.default} border rounded ${theme.colors.surface}`}>
                  <table className="w-full text-sm">
                    <thead className={`${theme.colors.background} sticky top-0`}>
                      <tr>
                        {queryResults.length > 0 && Object.keys(queryResults[0]).map((key) => (
                          <th key={key} className={`px-3 py-2 ${theme.border.default} border text-left font-medium ${theme.text.primary}`}>
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queryResults.slice(0, 1000).map((row, idx) => (
                        <tr key={idx} className={`hover:${theme.colors.backgroundHover}`}>
                          {Object.values(row).map((value, colIdx) => (
                            <td key={colIdx} className={`px-3 py-2 ${theme.border.default} border ${theme.text.primary}`}>
                              {value === null || value === undefined ? '—' : String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {queryResults.length > 1000 && (
                    <div className={`p-2 text-xs ${theme.text.muted} text-center ${theme.colors.background}`}>
                      Showing first 1000 of {queryResults.length} rows
                    </div>
                  )}
                </div>
              </div>
            )}

            {!queryResults && !queryLoading && (
              <div className={`flex-1 flex items-center justify-center ${theme.text.muted}`}>
                Enter a SQL query and click "Execute Query" to see results
              </div>
            )}
          </div>
        ) : null
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.colors.surface} rounded-lg ${theme.shadow.lg} p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${theme.border.light} border`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-semibold ${theme.text.primary}`}>
                {editingRecord ? 'Edit Record' : 'Add Record'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({});
                  setEditingRecord(null);
                }}
                className={`${theme.text.secondary} hover:${theme.text.primary}`}
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {tableColumns
                .filter((col) => {
                  // Skip auto-increment primary keys when adding
                  if (!editingRecord && col.isPrimaryKey && col.columnDefault?.includes('nextval')) {
                    return false;
                  }
                  return true;
                })
                .map((col) => {
                  const inputType = getInputType(col);
                  const value = formData[col.columnName] ?? '';

                  return (
                    <div key={col.columnName}>
                      <label className={`block text-sm font-medium mb-1 ${theme.text.primary}`}>
                        {col.columnName}
                        {!col.isNullable && col.isNullable !== 'YES' && (
                          <span className={`${theme.colors.danger} ml-1`}>*</span>
                        )}
                        {col.isPrimaryKey && <span className={`${theme.colors.primary} ml-1`}>(PK)</span>}
                      </label>
                      {inputType === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={value === true || value === 'true'}
                          onChange={(e) => handleInputChange(col.columnName, e.target.checked)}
                          className="w-5 h-5"
                        />
                      ) : inputType === 'datetime-local' ? (
                        <input
                          type="datetime-local"
                          value={value ? (value.includes('T') ? value.slice(0, 16) : new Date(value).toISOString().slice(0, 16)) : ''}
                          onChange={(e) => handleInputChange(col.columnName, e.target.value)}
                          className={`w-full px-3 py-2 ${theme.border.default} border rounded ${theme.text.primary}`}
                        />
                      ) : (
                        <input
                          type={inputType}
                          value={value}
                          onChange={(e) => {
                            const newValue = inputType === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                            handleInputChange(col.columnName, newValue);
                          }}
                          disabled={col.isPrimaryKey && editingRecord}
                          className={`w-full px-3 py-2 ${theme.border.default} border rounded disabled:${theme.colors.background} ${theme.text.primary}`}
                          required={!col.isNullable && col.isNullable !== 'YES'}
                        />
                      )}
                      <div className={`text-xs ${theme.text.muted} mt-1`}>
                        {col.dataType} {col.isNullable === 'YES' ? '(nullable)' : '(required)'}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({});
                  setEditingRecord(null);
                }}
                className={`px-4 py-2 ${theme.border.default} border rounded hover:${theme.colors.backgroundHover} ${theme.colors.surface} ${theme.text.primary} ${theme.shadow.sm}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded hover:opacity-90 ${theme.shadow.sm}`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseBrowserPage;

